import { Component, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { Observable, of } from 'rxjs';
import { DocxEditor } from './docx-editor';
import { DocxSkeleton } from './docx-skeleton';

export interface DocxDialogData {
  readonly file: File | Observable<File>;
  readonly edited: (file: File) => void;
  /** What the document is, e.g. "Ondersteuningsplan JW"; the file name when absent. */
  readonly title?: string;
}

@Component({
  selector: 'app-docx-dialog',
  imports: [
    TranslateModule,
    MatDialogModule,
    MatDividerModule,
    MatIconModule,
    MatButton,
    MatIconButton,
    DocxEditor,
    DocxSkeleton,
  ],
  templateUrl: './docx-dialog.html',
  styleUrl: './docx-dialog.scss',
})
export class DocxDialog {
  protected readonly data = inject<DocxDialogData>(MAT_DIALOG_DATA);

  private readonly dialogRef = inject(MatDialogRef<DocxDialog>);

  protected readonly file = signal<File | null>(null);

  private current: File | null = null;

  constructor() {
    const file$ = this.data.file instanceof File ? of(this.data.file) : this.data.file;

    file$.pipe(takeUntilDestroyed()).subscribe({
      next: (file) => {
        this.file.set(file);
        this.current = file;
      },
      error: () => this.close(),
    });
  }

  protected onEdited(file: File): void {
    this.current = file;
    this.data.edited(file);
  }

  protected download(): void {
    if (!this.current) {
      return;
    }

    const url = URL.createObjectURL(this.current);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = this.current.name;
    anchor.click();
    URL.revokeObjectURL(url);
  }

  protected close(): void {
    this.dialogRef.close();
  }
}
